import { gql } from '@apollo/client';
import {
  Box,
  IconButton,
  IconButtonProps,
  Menu,
  Typography,
} from '@mui/material';
import { CheckboxMenuItem } from 'components/common/form/Select';
import { DropDownMoreMenuItem } from 'components/common/Menu';
import { IconBoldInstagram } from 'components/icons/components/bold/IconBoldInstagram';
import { IconCustomTiktok } from 'components/icons/components/custom/IconCustomTiktok';
import { IconCustomYoutubeShorts } from 'components/icons/components/custom/IconCustomYoutubeShorts';
import { IconLinearArrowDown } from 'components/icons/components/linear/IconLinearArrowDown';
import { IconLinearMonitorMobile } from 'components/icons/components/linear/IconLinearMonitorMobile';
import { useFeatureFlagContext } from 'contexts/FeatureFlag.context';
import {
  Platform,
  PlotFeature,
  SocialPostType,
  useGetBrandByIdsForSlaAnalyticsFilterByPlatformQuery,
} from 'graphql/generated';
import { useCallback, useMemo, useState } from 'react';
import { theme } from 'styles/theme';
import { SLAAnalyticsFilterByPlatformCollapsibleItem } from './SLAAnalyticsFilterByPlatformCollapsibleItem';

// eslint-disable-next-line
gql`
  query GetBrandByIdsForSLAAnalyticsFilterByPlatform(
    $input: GetBrandByIdsInput!
  ) {
    brandByIds(input: $input) {
      id
      creators {
        id
        ...CreatorFragmentSLAAnalyticsFilterByPlatformCollapsibleItem
      }
    }
  }
`;

interface SLAAnalyticsFilterByPlatformProps {
  selectedPlatforms?: Platform[];
  selectedTypes?: SocialPostType[];
  selectedCreatorIds?: string[];
  renderTitle?: () => React.ReactNode;
  brandIdsForCreatorHandles?: string[];
  onChange: (values: {
    selectedPlatforms: Platform[];
    selectedTypes: SocialPostType[];
    selectedCreatorIds: string[];
  }) => void;
  hideOptions?: {
    creatorHandles?: boolean;
    postTypes?: boolean;
  };
  componentsProps?: {
    iconButton?: IconButtonProps;
  };
}

export const SLAAnalyticsFilterByPlatform = ({
  selectedPlatforms = [],
  selectedTypes = [],
  selectedCreatorIds = [],
  renderTitle,
  onChange,
  brandIdsForCreatorHandles = [],
  hideOptions,
  componentsProps,
}: SLAAnalyticsFilterByPlatformProps) => {
  const { isFeatureEnabled } = useFeatureFlagContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // get creators by brand ids => use it to render creator handles and post types
  const { data: brandByIdsData } =
    useGetBrandByIdsForSlaAnalyticsFilterByPlatformQuery({
      variables: {
        input: {
          brandIds: brandIdsForCreatorHandles,
        },
      },
      skip: !brandIdsForCreatorHandles.length,
    });

  const [collapsedSections, setCollapsedSections] = useState<Platform[]>(
    Object.keys(Platform) as Platform[],
  );

  const { instagramCreators, tiktokCreators, youtubeCreators, allCreators } =
    useMemo(() => {
      const mergedCreators =
        brandByIdsData?.brandByIds?.flatMap((brand) => brand.creators) ?? [];

      // filter creators by platform
      const instagramCreators = mergedCreators.filter(
        (creator) => creator.platform === Platform.Instagram,
      );
      const tiktokCreators = mergedCreators.filter(
        (creator) => creator.platform === Platform.Tiktok,
      );
      const youtubeCreators = mergedCreators.filter(
        (creator) => creator.platform === Platform.Youtube,
      );

      return {
        tiktokCreators,
        instagramCreators,
        youtubeCreators,
        allCreators: mergedCreators,
      };
    }, [brandByIdsData]);

  // render list of selected platforms with format: Platform1 & Platform2 & ...
  const renderLabel = useMemo(() => {
    if (renderTitle) {
      return renderTitle();
    }
    return (
      <Box
        sx={{
          display: 'flex',
          gap: theme.spacing(2),
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(35, 6, 3, 0.05)',
            borderRadius: theme.spacing(1),
            padding: theme.spacing(1),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconLinearMonitorMobile size={16} />
        </Box>

        <Typography variant="subhead-xl">
          {selectedPlatforms.length
            ? selectedPlatforms.join(' & ')
            : 'Platforms'}
        </Typography>
        <IconLinearArrowDown size={16} color={theme.colors?.utility[700]} />
      </Box>
    );
  }, [renderTitle, selectedPlatforms]);

  const onToggleCollapse = (platform: Platform) => {
    if (collapsedSections.includes(platform)) {
      setCollapsedSections((prev) => prev.filter((o) => o !== platform));
    } else {
      setCollapsedSections((prev) => [...prev, platform]);
    }
  };

  const onSelectPlatformCheckbox = useCallback(
    (platform: Platform) => {
      const creatorIdsByPlatform = allCreators
        .filter((creator) => creator.platform === platform)
        .map((creator) => creator.id);
      if (selectedPlatforms.includes(platform)) {
        // selected => remove it
        let _selectedTypes = [...selectedTypes];
        const _selectedCreatorIds = selectedCreatorIds.filter(
          (id) => !creatorIdsByPlatform.includes(id),
        );
        if (platform === Platform.Instagram) {
          // remove all post types
          _selectedTypes = [];
        }
        onChange({
          selectedPlatforms: selectedPlatforms.filter((o) => o !== platform),
          selectedTypes: _selectedTypes,
          selectedCreatorIds: _selectedCreatorIds,
        });
      } else {
        // not selected => add it
        let _selectedTypes: SocialPostType[] = selectedTypes;
        if (platform === Platform.Instagram) {
          // if instagram is selected, select all post types
          _selectedTypes = Object.values(SocialPostType);
        }

        const _selectedCreatorIds = Array.from(
          new Set([...selectedCreatorIds, ...creatorIdsByPlatform]),
        );

        const _selectedPlatforms = Array.from(
          new Set([...selectedPlatforms, platform]),
        );

        onChange({
          selectedPlatforms: _selectedPlatforms,
          selectedTypes: _selectedTypes,
          selectedCreatorIds: _selectedCreatorIds,
        });
      }
    },
    [
      allCreators,
      onChange,
      selectedCreatorIds,
      selectedPlatforms,
      selectedTypes,
    ],
  );

  const onSelectCreatorCheckbox = useCallback(
    (creatorId: string, platform: Platform) => {
      const isSelected: boolean = selectedCreatorIds.includes(creatorId);
      const action = isSelected ? 'deselect' : 'select';
      const updatedSelectedCreatorIds =
        action === 'select'
          ? [...selectedCreatorIds, creatorId]
          : selectedCreatorIds.filter((o) => o !== creatorId);

      // selecting a creator when the platform is not selected should also select the platform
      const updatedSelectedPlatforms =
        action === 'select'
          ? Array.from(new Set([...selectedPlatforms, platform]))
          : selectedPlatforms;

      onChange({
        selectedPlatforms: updatedSelectedPlatforms,
        selectedTypes,
        selectedCreatorIds: updatedSelectedCreatorIds,
      });
    },
    [onChange, selectedCreatorIds, selectedPlatforms, selectedTypes],
  );

  const renderPostTypesList = useCallback(() => {
    return (
      <Box>
        <Box
          sx={{
            py: 2,
            pl: 3,
            ...(!instagramCreators.length ? { pt: 0 } : {}),
          }}
        >
          <Typography variant="subhead-sm" color={theme.colors?.utility[700]}>
            Post Type
          </Typography>
        </Box>
        {(
          [
            [SocialPostType.Video, 'Reels'],
            [SocialPostType.Photo, 'Photos'],
            [SocialPostType.Carousel, 'Carousel'],
          ] as [SocialPostType, string][]
        ).map(([type, label]) => {
          const isChecked = selectedTypes.includes(type);
          return (
            <CheckboxMenuItem
              key={type}
              checkboxPosition="end"
              label={label}
              value={type}
              checked={isChecked}
              onClick={() => {
                if (isChecked) {
                  // if selected => just remove this type
                  onChange({
                    selectedPlatforms,
                    selectedTypes: selectedTypes.filter((o) => o !== type),
                    selectedCreatorIds,
                  });
                } else {
                  // if not selected => add this type, AND add Instagram to the selected platforms
                  onChange({
                    selectedPlatforms: Array.from(
                      new Set([...selectedPlatforms, Platform.Instagram]),
                    ),
                    selectedTypes: [...selectedTypes, type],
                    selectedCreatorIds,
                  });
                }
              }}
            />
          );
        })}
      </Box>
    );
  }, [
    instagramCreators,
    onChange,
    selectedCreatorIds,
    selectedPlatforms,
    selectedTypes,
  ]);

  return (
    <>
      <IconButton
        disableRipple
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        {...componentsProps?.iconButton}
        sx={{
          bgcolor: theme.colors?.utility[275],
          borderRadius: theme.spacing(2),
          p: theme.spacing(2),
          color: theme.colors?.primary.black,
          ...componentsProps?.iconButton?.sx,
        }}
      >
        {renderLabel}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 244,
            padding: 4,
            background: 'rgba(255, 255, 255, 0.80)',
            backdropFilter: 'blur(20px)',
            boxShadow:
              '0px 8px 18px -6px rgba(24, 39, 75, 0.12), 0px 12px 42px -4px rgba(24, 39, 75, 0.12)',
            border: 'none',
            '& ul > :first-child': {
              display: 'none',
            },
          },
        }}
      >
        <DropDownMoreMenuItem
          disableRipple
          defaultOpen
          label=""
          componentsProps={{
            dropDownOptions: {
              container: {
                sx: {
                  gap: 2,
                },
              },
            },
          }}
          dropDownOptions={[
            {
              platform: Platform.Instagram,
              icon: IconBoldInstagram,
              label: 'Instagram',
              creatorHandles: instagramCreators,
              // render post types list as the sub items of the platform
              // if any of the post types is selected, the platform Instagram should be selected
              renderExtendItems: hideOptions?.postTypes
                ? undefined
                : renderPostTypesList,
            },
            {
              platform: Platform.Tiktok,
              icon: IconCustomTiktok,
              label: 'Tiktok',
              creatorHandles: tiktokCreators,
            },
            ...(isFeatureEnabled(PlotFeature.YoutubeParsing)
              ? [
                  {
                    platform: Platform.Youtube,
                    icon: IconCustomYoutubeShorts,
                    label: 'Youtube Shorts',
                    creatorHandles: youtubeCreators,
                  },
                ]
              : []),
          ].map(
            ({ platform, icon, label, creatorHandles, renderExtendItems }) => {
              return {
                label,
                value: platform,
                renderOption: () => {
                  const isCollapseOpen = collapsedSections.includes(platform);
                  const isPlatformSelected =
                    selectedPlatforms.includes(platform);
                  return (
                    <SLAAnalyticsFilterByPlatformCollapsibleItem
                      key={platform}
                      platform={platform}
                      icon={icon}
                      label={label}
                      isCollapseOpen={isCollapseOpen}
                      isPlatformSelected={isPlatformSelected}
                      selectedCreatorIds={selectedCreatorIds}
                      onToggleCollapse={() => onToggleCollapse(platform)}
                      onTogglePlatformCheckbox={() =>
                        onSelectPlatformCheckbox(platform)
                      }
                      onToggleCreatorCheckbox={(creatorId) =>
                        onSelectCreatorCheckbox(creatorId, platform)
                      }
                      hideCreatorHandles={hideOptions?.creatorHandles}
                      creatorHandles={creatorHandles}
                      renderExtendItems={renderExtendItems}
                    />
                  );
                },
              };
            },
          )}
        />
      </Menu>
    </>
  );
};
